/* Cotton rates — one variety's price, large, with the others under it.
 *
 * CAI official spot, lint, ₹ per candy. Chips filter by staple length, the
 * headline is the chosen variety, the line is its last forty quotes, and
 * the table is every variety at the chosen staple — tap a row to make it
 * the headline.
 *
 * One departure from the design: it read "the latest date on file" and then
 * showed only the varieties quoted on exactly that date. CAI does not quote
 * every variety every day, so on real data that leaves two or three rows
 * out of twenty-one. Each row carries its own quote date instead, and the
 * header says the newest.
 */

import type { MobileSummary } from '../../lib/api_mobile'
import { MobileTable, type MRow } from '../MobileTable'
import { Head, IN, LineChart, NoteBox, RS, SG } from '../parts'
import { Blueprint } from '../../components/ui'

const DAY = (iso: string) => new Date(iso + 'T00:00:00')
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

export function Cotton({ data, staple, onStaple, pick, onPick }: {
  data: MobileSummary
  /** 'all', or a staple length in mm as a string. */
  staple: string
  onStaple: (s: string) => void
  /** The variety key (`k`) in the headline. */
  pick: string | null
  onPick: (k: string) => void
}) {
  const rates = data.rates
  if (!rates.length) {
    return <NoteBox>No cotton rates on file. Fetch them on the web app's Cotton page.</NoteBox>
  }

  const staples = [...new Set(rates.map((r) => r.mm).filter((m): m is number => m != null))]
    .sort((a, b) => a - b)
  const shown = staple === 'all' ? rates : rates.filter((r) => String(r.mm) === staple)
  // Until one is tapped, the headline is the most recently quoted variety
  // of those in view — not the first by staple, which may be a month stale.
  const freshest = (rs: typeof rates) =>
    rs.reduce<typeof rates[number] | undefined>((m, r) => (!m || r.d > m.d ? r : m), undefined)
  const rate = shown.find((r) => r.k === pick) ?? freshest(shown) ?? rates[0]
  const newest = rates.reduce((m, r) => (r.d > m ? r.d : m), rates[0].d)

  const change = (now: number, then: number | null) =>
    then == null ? '–'
      : `${SG(now - then, IN)} · ${SG((now - then) / then * 100, (x) => x.toFixed(1))}%`

  const lo = Math.min(...rate.trend) * 0.99
  const hi = Math.max(...rate.trend) * 1.01

  const rows: MRow[] = shown.map((r) => ({
    sel: r.k === rate.k,
    on: () => onPick(r.k),
    cells: [
      { v: r.k, sub: r.d === newest ? r.std : `${r.std} · ${DAY(r.d)}` },
      { v: IN(r.last) },
      { v: r.wk == null ? '–' : SG(r.last - r.wk, IN), s: r.wk == null ? 0 : r.last - r.wk,
        color: 'var(--color-neutral-800)' },
      { v: r.mo == null ? '–' : SG(r.last - r.mo, IN), s: r.mo == null ? 0 : r.last - r.mo,
        color: 'var(--color-neutral-800)' },
      { v: r.mm == null ? '–' : `${r.mm} mm`, s: r.mm ?? 0 },
    ],
  }))

  return (
    <>
      <div className="m-label" style={{ padding: '12px 0 2px' }}>
        CAI official spot · lint · newest quote {DAY(newest)}
      </div>

      <div className="m-label" style={{ marginTop: 14 }}>Staple length</div>
      <div className="m-rail m-noscroll" style={{ gap: 6, marginTop: 6 }}>
        {[{ k: 'all', l: 'All' }, ...staples.map((m) => ({ k: String(m), l: `${m} mm` }))]
          .map((c) => {
            const on = staple === c.k
            return (
              <button key={c.k} type="button" onClick={() => onStaple(c.k)}
                style={{
                  flex: 'none', minHeight: 40, padding: '0 14px',
                  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
                  background: on ? 'var(--color-accent)' : 'transparent',
                  color: on ? '#fff' : 'var(--color-text)',
                  font: '600 14px var(--font-heading)', whiteSpace: 'nowrap', cursor: 'pointer',
                }}>{c.l}</button>
            )
          })}
      </div>

      <div className="m-eyebrow" style={{ marginTop: 18 }}>{rate.region}</div>
      <div style={{
        fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 600, marginTop: 4,
      }}>{rate.k}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
        <span className="m-num" style={{
          fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 52, lineHeight: 1,
        }}>{RS(rate.last)}</span>
        <span className="m-sub">per candy</span>
      </div>
      {rate.d !== newest && (
        <div className="m-aside" style={{ marginTop: 6 }}>
          Last quoted {DAY(rate.d)} — not every variety is quoted every day.
        </div>
      )}

      <div className="m-grid-2" style={{ marginTop: 16 }}>
        {[{ l: '1 week', v: change(rate.last, rate.wk) },
          { l: '1 month', v: change(rate.last, rate.mo) }].map((k) => (
            <Blueprint key={k.l} style={{ padding: '10px 12px' }}>
              <div className="m-label">{k.l}</div>
              <div className="m-num" style={{
                fontFamily: 'var(--font-heading)', fontSize: 21, marginTop: 4,
              }}>{k.v}</div>
            </Blueprint>
          ))}
      </div>

      <Head title={`Last ${rate.trend.length} quotes`} aside="₹ / candy" />
      <LineChart series={rate.trend} lo={lo} hi={hi} fmt={IN}
        from={`${rate.trend.length} quotes ago`} to={DAY(rate.d)} />

      <Head title={staple === 'all' ? 'All varieties' : `${staple} mm varieties`}
        aside={`${shown.length} · tap to chart`} />
      <MobileTable cols={[
        { l: 'Variety', w: 150 }, { l: '₹ / candy', w: 84 },
        { l: '1 wk', w: 70 }, { l: '1 mo', w: 70 }, { l: 'Staple', w: 64 },
      ]} rows={rows} />
    </>
  )
}
